import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  Fab,
  Badge,
  Chip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  MonitorHeart as MonitorIcon,
  Science as ScienceIcon,
  Analytics as AnalyticsIcon,
  Calculate as CalculateIcon,
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { RootState } from '../../store';
import { WorkflowStage } from '../../store/slices/workflowSlice';

// Import our enhanced components
import RealTimeWaterQualityMonitor from './RealTimeWaterQualityMonitor';
import WaterQualityAnalysisV2 from './WaterQualityAnalysisV2';
import WaterQualityInputUpdated from './WaterQualityInputUpdated';
import WaterProfileSelector from './WaterProfileSelector';
import WaterProfileAnalysis from './WaterProfileAnalysis';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`water-quality-tabpanel-${index}`}
      aria-labelledby={`water-quality-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `water-quality-tab-${index}`,
    'aria-controls': `water-quality-tabpanel-${index}`,
  };
}

interface Alert {
  id: string;
  message: string;
  severity: 'warning' | 'error' | 'info' | 'success';
  timestamp: Date;
}

interface WaterQualityDashboardProps {
  onWaterProfileSave?: (waterData: any) => void;
  onAnalysisComplete?: (results: any) => void;
}

const WaterQualityDashboard: React.FC<WaterQualityDashboardProps> = ({
  onWaterProfileSave,
  onAnalysisComplete
}) => {
  // Get workflow state
  const workflowState = useSelector((state: RootState) => state.workflow);
  const isStage1Completed = workflowState.stageStatus[WorkflowStage.WATER_PROFILE]?.completed || false;
  const workflowWaterProfile = workflowState.waterProfile;

  const [activeTab, setActiveTab] = useState(0);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [currentWaterData, setCurrentWaterData] = useState<any>(null);
  const [selectedWaterProfile, setSelectedWaterProfile] = useState<any>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleParameterAlert = useCallback((parameter: string, value: number, status: 'warning' | 'error') => {
    const newAlert: Alert = {
      id: `${parameter}-${Date.now()}`,
      message: `${parameter.toUpperCase()} is ${status === 'error' ? 'critically' : ''} out of range: ${value}`,
      severity: status,
      timestamp: new Date()
    };

    setAlerts(prev => [newAlert, ...prev.slice(0, 9)]); // Keep last 10 alerts
    setSnackbarOpen(true);
  }, []);

  const handleWaterProfileSelect = useCallback((profile: any) => {
    setSelectedWaterProfile(profile);
    setShowCreateForm(false);

    if (profile) {
      // Convert database profile to component format
      const waterData = {
        id: profile.id,
        name: profile.name,
        ph: profile.ph,
        ec: profile.ec,
        // Convert elements array to individual properties
        ca: profile.elements?.find((e: any) => e.elementSymbol === 'Ca')?.concentration || 0,
        mg: profile.elements?.find((e: any) => e.elementSymbol === 'Mg')?.concentration || 0,
        k: profile.elements?.find((e: any) => e.elementSymbol === 'K')?.concentration || 0,
        na: profile.elements?.find((e: any) => e.elementSymbol === 'Na')?.concentration || 0,
        cl: profile.elements?.find((e: any) => e.elementSymbol === 'Cl')?.concentration || 0,
        so4: profile.elements?.find((e: any) => e.elementSymbol === 'S')?.concentration || 0,
        no3: profile.elements?.find((e: any) => e.elementSymbol === 'N')?.concentration || 0,
        hco3: profile.elements?.find((e: any) => e.elementSymbol === 'HCO3')?.concentration || 0,
        co3: profile.elements?.find((e: any) => e.elementSymbol === 'CO3')?.concentration || 0,
        tds: 0, // Calculate or default
        temperature: 20 // Default
      };

      setCurrentWaterData(waterData);

      // Call workflow callback if provided
      if (onWaterProfileSave) {
        onWaterProfileSave(waterData);
      }

      // Show selection notification
      const selectionAlert: Alert = {
        id: `select-${Date.now()}`,
        message: `Water profile "${profile.name}" selected`,
        severity: 'info',
        timestamp: new Date()
      };

      setAlerts(prev => [selectionAlert, ...prev.slice(0, 9)]);
      setSnackbarOpen(true);
    }
  }, [onWaterProfileSave]);

  const handleCreateNewProfile = useCallback(() => {
    setShowCreateForm(true);
    setSelectedWaterProfile(null);
    setCurrentWaterData(null);
  }, []);

  const handleWaterDataSave = useCallback((data: any) => {
    setCurrentWaterData(data);

    // Show success notification
    const successAlert: Alert = {
      id: `save-${Date.now()}`,
      message: `Water profile "${data.name}" saved successfully`,
      severity: 'success',
      timestamp: new Date()
    };

    setAlerts(prev => [successAlert, ...prev.slice(0, 9)]);
    setSnackbarOpen(true);

    // Call workflow callback if provided
    if (onWaterProfileSave) {
      onWaterProfileSave(data);
    }
  }, [onWaterProfileSave]);

  const handlePHCalculationComplete = useCallback((result: any) => {
    // Show calculation complete notification
    const calculationAlert: Alert = {
      id: `calculation-${Date.now()}`,
      message: `pH adjustment calculated: ${result.amount?.toFixed(2)} mL of ${result.product?.name || 'selected product'}`,
      severity: 'info',
      timestamp: new Date()
    };

    setAlerts(prev => [calculationAlert, ...prev.slice(0, 9)]);
    setSnackbarOpen(true);
  }, []);

  const handleAnalyzeWaterQuality = useCallback(() => {
    // Switch to analysis tab when analyze is triggered
    setActiveTab(2);

    // Call analysis callback if provided
    if (onAnalysisComplete && currentWaterData) {
      // Simulate analysis results for now
      const analysisResults = {
        overallScore: 85,
        recommendations: ['pH within optimal range', 'Consider reducing sodium levels'],
        compatibility: 'good'
      };
      onAnalysisComplete(analysisResults);
    }
  }, [onAnalysisComplete, currentWaterData]);

  const getTabIcon = (index: number) => {
    const icons = [
      <DashboardIcon />,
      <MonitorIcon />,
      <AnalyticsIcon />
    ];
    return icons[index];
  };

  const recentAlerts = alerts.filter(alert => 
    alert.severity === 'warning' || alert.severity === 'error'
  );

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Paper elevation={1} sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1">
            Water Quality Management
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Workflow Status */}
            {isStage1Completed && workflowWaterProfile && (
              <Chip
                icon={<CheckCircleIcon />}
                label={`Stage 1 Complete: ${workflowWaterProfile.name}`}
                color="success"
                variant="filled"
                sx={{ fontWeight: 'medium' }}
              />
            )}

            {currentWaterData && !isStage1Completed && (
              <Typography variant="body2" color="text.secondary">
                Active Profile: {currentWaterData.name}
              </Typography>
            )}

            <Badge badgeContent={recentAlerts.length} color="error">
              <Fab
                size="small"
                color="primary"
                onClick={() => setSnackbarOpen(true)}
                disabled={alerts.length === 0}
              >
                <NotificationsIcon />
              </Fab>
            </Badge>
          </Box>
        </Box>
      </Paper>

      {/* Navigation Tabs */}
      <Paper elevation={1} sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="water quality dashboard tabs"
          variant="fullWidth"
        >
          <Tab
            icon={getTabIcon(0)}
            label="Input & Setup"
            {...a11yProps(0)}
          />
          <Tab
            icon={getTabIcon(1)}
            label="Real-Time Monitor"
            {...a11yProps(1)}
          />
          <Tab
            icon={getTabIcon(2)}
            label="Analysis & Reports"
            {...a11yProps(2)}
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <TabPanel value={activeTab} index={0}>
        <Grid container spacing={3}>
          <Grid size={12}>
            <WaterProfileSelector
              onProfileSelect={handleWaterProfileSelect}
              onCreateNew={handleCreateNewProfile}
              selectedProfileId={selectedWaterProfile?.id}
            />
          </Grid>

          {showCreateForm && (
            <Grid size={12}>
              <WaterQualityInputUpdated
                onSave={handleWaterDataSave}
                onAnalyze={handleAnalyzeWaterQuality}
              />
            </Grid>
          )}
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Grid container spacing={3}>
          <Grid size={12}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Development Add-on - Future Enhancement</strong>
              </Typography>
              <Typography variant="body2">
                This feature will use IoT sensors for live water quality monitoring.
                Currently showing simulated data for demonstration purposes.
              </Typography>
            </Alert>
          </Grid>
          <Grid size={12}>
            <RealTimeWaterQualityMonitor
              autoRefresh={true}
              refreshInterval={5000}
              onParameterAlert={handleParameterAlert}
            />
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <Grid container spacing={3}>
          <Grid size={12}>
            <WaterProfileAnalysis />
          </Grid>
        </Grid>
      </TabPanel>



      {/* Notifications Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={alerts[0]?.severity || 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {alerts[0]?.message || 'No new notifications'}
        </Alert>
      </Snackbar>

      {/* Recent Alerts Summary */}
      {recentAlerts.length > 0 && (
        <Paper 
          elevation={2} 
          sx={{ 
            position: 'fixed', 
            bottom: 16, 
            left: 16, 
            p: 2, 
            maxWidth: 300,
            zIndex: 1000
          }}
        >
          <Typography variant="subtitle2" gutterBottom>
            Recent Alerts ({recentAlerts.length})
          </Typography>
          {recentAlerts.slice(0, 3).map((alert) => (
            <Alert key={alert.id} severity={alert.severity} sx={{ mb: 1, fontSize: '0.75rem' }}>
              {alert.message}
            </Alert>
          ))}
          {recentAlerts.length > 3 && (
            <Typography variant="caption" color="text.secondary">
              +{recentAlerts.length - 3} more alerts
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default WaterQualityDashboard;
